<script setup>
import { ref, computed } from 'vue';
import ForwardToOption from './emailChannels/ForwardToOption.vue';
import Microsoft from './emailChannels/Microsoft.vue';
import Google from './emailChannels/Google.vue';
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';
import { toneForChannelKey } from 'dashboard/helper/channelTone';

import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

const provider = ref('');

const getters = useStoreGetters();
const { t } = useI18n();

const globalConfig = getters['globalConfig/get'];
const isATolaInstance = getters['globalConfig/isATolaInstance'];

const emailProviderList = computed(() => {
  return [
    {
      title: t('INBOX_MGMT.EMAIL_PROVIDERS.MICROSOFT.TITLE'),
      description: t('INBOX_MGMT.EMAIL_PROVIDERS.MICROSOFT.DESCRIPTION'),
      isEnabled: !!globalConfig.value.azureAppId,
      key: 'microsoft',
      icon: 'i-woot-outlook',
    },
    {
      title: t('INBOX_MGMT.EMAIL_PROVIDERS.GOOGLE.TITLE'),
      description: t('INBOX_MGMT.EMAIL_PROVIDERS.GOOGLE.DESCRIPTION'),
      isEnabled: !!window.tolaConfig.googleOAuthClientId,
      key: 'google',
      icon: 'i-woot-gmail',
    },
    {
      title: t('INBOX_MGMT.EMAIL_PROVIDERS.OTHER_PROVIDERS.TITLE'),
      description: t('INBOX_MGMT.EMAIL_PROVIDERS.OTHER_PROVIDERS.DESCRIPTION'),
      isEnabled: true,
      key: 'other_provider',
      icon: 'i-woot-mail',
    },
  ].filter(providerConfig => {
    if (isATolaInstance.value) {
      return true;
    }
    return providerConfig.isEnabled;
  });
});

function onClick(emailProvider) {
  if (emailProvider.isEnabled) {
    provider.value = emailProvider.key;
  }
}
</script>

<template>
  <section
    v-if="!provider"
    class="w-full max-w-3xl px-4 py-6 mx-auto sm:px-6 sm:py-8"
    aria-labelledby="email-provider-title"
  >
    <div class="max-w-2xl">
      <p class="mb-2 text-xs font-semibold text-n-violet-11">
        {{ $t('INBOX_MGMT.CREATE_FLOW.BUILDER.STEP_LABEL', { step: 2 }) }}
      </p>
      <h2
        id="email-provider-title"
        class="text-2xl font-semibold tracking-tight text-n-slate-12"
      >
        {{ $t('INBOX_MGMT.ADD.EMAIL_PROVIDER.TITLE') }}
      </h2>
      <p class="mt-2 mb-0 text-sm leading-6 text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.EMAIL_PROVIDER.DESCRIPTION') }}
      </p>
    </div>
    <div class="grid grid-cols-1 gap-4 mx-0 mt-7 sm:grid-cols-3">
      <ChannelSelector
        v-for="emailProvider in emailProviderList"
        :key="emailProvider.key"
        :title="emailProvider.title"
        :description="emailProvider.description"
        :icon="emailProvider.icon"
        :tone="toneForChannelKey(emailProvider.key)"
        :disabled="!emailProvider.isEnabled"
        @click="() => onClick(emailProvider)"
      />
    </div>
  </section>
  <Microsoft v-else-if="provider === 'microsoft'" />
  <Google v-else-if="provider === 'google'" />
  <ForwardToOption v-else-if="provider === 'other_provider'" />
</template>
